import { useEffect, useState } from 'react'

import { getDealListingImport } from '@/lib/api'

const ACTIVE_STATUSES = new Set(['queued', 'running'])
const POLL_INTERVAL_MS = 1_500
const WAIT_LIMIT_MS = 35_000

function pollingActive(listingImport, pollImageFallback) {
  return ACTIVE_STATUSES.has(listingImport?.status) ||
    (pollImageFallback && listingImport?.imageFallbackPending === true)
}

export function useListingImport({
  dealId,
  initialImport,
  enabled,
  csrfToken,
  pollImageFallback = false,
}) {
  const [polledImport, setPolledImport] = useState(null)
  const [elapsed, setElapsed] = useState(null)
  const [exceededWait, setExceededWait] = useState(null)
  const [pollingError, setPollingError] = useState(null)
  const currentImportValue = (record, fallback) => {
    if (!record || record.dealId !== dealId || record.initialImport !== initialImport) return fallback

    return record.value
  }
  const listingImport = currentImportValue(polledImport, initialImport ?? null)
  const elapsedMs = currentImportValue(elapsed, 0)
  const hasExceededWait = currentImportValue(exceededWait, false)
  const error = currentImportValue(pollingError, null)

  useEffect(() => {
    if (!enabled || !dealId) return undefined

    let ignore = false
    let requestInFlight = false
    let intervalId
    let waitTimeoutId
    const startedAt = Date.now()
    let currentImport = initialImport

    const clearTimers = () => {
      if (intervalId) clearInterval(intervalId)
      if (waitTimeoutId) clearTimeout(waitTimeoutId)
    }

    const poll = async () => {
      if (requestInFlight || !pollingActive(currentImport, pollImageFallback)) return

      requestInFlight = true
      try {
        const payload = await getDealListingImport(dealId, { csrfToken })
        if (ignore) return

        const nextImport = payload.listingImport
        currentImport = nextImport
        setPolledImport({ dealId, initialImport, value: nextImport })
        setPollingError({ dealId, initialImport, value: null })
        if (!pollingActive(currentImport, pollImageFallback)) clearTimers()
      } catch (pollError) {
        if (!ignore) setPollingError({ dealId, initialImport, value: pollError })
      } finally {
        requestInFlight = false
      }
    }

    if (pollingActive(currentImport, pollImageFallback)) {
      intervalId = setInterval(() => {
        if (!ignore) setElapsed({ dealId, initialImport, value: Date.now() - startedAt })
        void poll()
      }, POLL_INTERVAL_MS)
      waitTimeoutId = setTimeout(() => {
        if (ignore) return

        setElapsed({ dealId, initialImport, value: WAIT_LIMIT_MS })
        setExceededWait({ dealId, initialImport, value: true })
      }, WAIT_LIMIT_MS)
    }

    return () => {
      ignore = true
      clearTimers()
    }
  }, [csrfToken, dealId, enabled, initialImport, pollImageFallback])

  return { listingImport, elapsedMs, hasExceededWait, error }
}
